import React, { useState, useEffect } from 'react';
import { Activity, User, FileText, Calendar, MessageSquare, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { supabase } from '../../lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ActivityEvent {
  id: string;
  type: 'user' | 'project' | 'document' | 'meeting' | 'message';
  action: string;
  description: string;
  timestamp: string;
  user?: string;
  metadata?: any;
}

export const LiveActivityFeed: React.FC = () => {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadRecentActivities();
  }, []);

  useRealtimeSubscription({
    table: 'audit_logs',
    event: 'INSERT',
    onInsert: (payload) => {
      addActivityFromAuditLog(payload.new);
    },
  });

  useRealtimeSubscription({
    table: 'projects',
    event: '*',
    onChange: (payload) => {
      if (payload.eventType === 'INSERT') {
        addActivity({
          id: crypto.randomUUID(),
          type: 'project',
          action: 'created',
          description: `Nouveau projet créé: ${payload.new.name}`,
          timestamp: new Date().toISOString(),
        });
      } else if (payload.eventType === 'UPDATE') {
        const statusChanged = payload.old.status !== payload.new.status;
        if (statusChanged) {
          addActivity({
            id: crypto.randomUUID(),
            type: 'project',
            action: 'status_changed',
            description: `Statut du projet "${payload.new.name}" changé: ${payload.new.status}`,
            timestamp: new Date().toISOString(),
          });
        }
      }
    },
  });

  useRealtimeSubscription({
    table: 'users',
    event: 'INSERT',
    onInsert: (payload) => {
      addActivity({
        id: crypto.randomUUID(),
        type: 'user',
        action: 'registered',
        description: `Nouvel utilisateur inscrit: ${payload.new.full_name || 'Utilisateur'}`,
        timestamp: new Date().toISOString(),
      });
    },
  });

  useRealtimeSubscription({
    table: 'project_documents',
    event: 'INSERT',
    onInsert: (payload) => {
      addActivity({
        id: crypto.randomUUID(),
        type: 'document',
        action: 'uploaded',
        description: `Document téléversé: ${payload.new.file_name}`,
        timestamp: new Date().toISOString(),
      });
    },
  });

  useRealtimeSubscription({
    table: 'meetings',
    event: 'INSERT',
    onInsert: (payload) => {
      addActivity({
        id: crypto.randomUUID(),
        type: 'meeting',
        action: 'scheduled',
        description: `Nouveau RDV planifié: ${payload.new.title}`,
        timestamp: new Date().toISOString(),
      });
    },
  });

  const loadRecentActivities = async () => {
    try {
      setIsLoading(true);
      const activities: ActivityEvent[] = [];

      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

      const { data: recentProjects } = await supabase
        .from('projects')
        .select('id, name, created_at, status')
        .gte('created_at', oneHourAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(5);

      if (recentProjects) {
        recentProjects.forEach(project => {
          activities.push({
            id: project.id,
            type: 'project',
            action: 'created',
            description: `Nouveau projet: ${project.name}`,
            timestamp: project.created_at,
          });
        });
      }

      const { data: recentUsers } = await supabase
        .from('users')
        .select('id, full_name, created_at')
        .gte('created_at', oneHourAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(5);

      if (recentUsers) {
        recentUsers.forEach(user => {
          activities.push({
            id: user.id,
            type: 'user',
            action: 'registered',
            description: `Nouvel utilisateur: ${user.full_name || 'Utilisateur'}`,
            timestamp: user.created_at,
          });
        });
      }

      const { data: recentMeetings } = await supabase
        .from('meetings')
        .select('id, title, created_at')
        .gte('created_at', oneHourAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(5);

      if (recentMeetings) {
        recentMeetings.forEach(meeting => {
          activities.push({
            id: meeting.id,
            type: 'meeting',
            action: 'scheduled',
            description: `RDV planifié: ${meeting.title}`,
            timestamp: meeting.created_at,
          });
        });
      }

      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setActivities(activities.slice(0, 20));
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addActivityFromAuditLog = (log: any) => {
    const activity: ActivityEvent = {
      id: log.id,
      type: getActivityType(log.table_name),
      action: log.action.toLowerCase(),
      description: formatAuditLogDescription(log),
      timestamp: log.created_at,
    };
    addActivity(activity);
  };

  const addActivity = (activity: ActivityEvent) => {
    setActivities(prev => [activity, ...prev].slice(0, 20));
  };

  const getActivityType = (tableName: string): ActivityEvent['type'] => {
    if (tableName.includes('user')) return 'user';
    if (tableName.includes('project')) return 'project';
    if (tableName.includes('document')) return 'document';
    if (tableName.includes('meeting') || tableName.includes('appointment')) return 'meeting';
    if (tableName.includes('message')) return 'message';
    return 'project';
  };

  const formatAuditLogDescription = (log: any): string => {
    const action = log.action === 'INSERT' ? 'créé' : log.action === 'UPDATE' ? 'modifié' : 'supprimé';
    return `${log.table_name} ${action}`;
  };

  const getActivityIcon = (type: ActivityEvent['type'], action: string) => {
    switch (type) {
      case 'user':
        return <User className="w-4 h-4" />;
      case 'project':
        return <FileText className="w-4 h-4" />;
      case 'document':
        return <FileText className="w-4 h-4" />;
      case 'meeting':
        return <Calendar className="w-4 h-4" />;
      case 'message':
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type: ActivityEvent['type']) => {
    switch (type) {
      case 'user':
        return 'bg-blue-100 text-blue-600';
      case 'project':
        return 'bg-green-100 text-green-600';
      case 'document':
        return 'bg-orange-100 text-orange-600';
      case 'meeting':
        return 'bg-purple-100 text-purple-600';
      case 'message':
        return 'bg-pink-100 text-pink-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <h3 className="text-lg font-semibold text-gray-900">Activité en direct</h3>
          </div>
          <span className="text-xs text-gray-500">Mis à jour en temps réel</span>
        </div>
      </div>

      <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
        {activities.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucune activité récente</p>
          </div>
        ) : (
          activities.map((activity) => (
            <div key={activity.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                  {getActivityIcon(activity.type, activity.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(activity.timestamp), {
                        addSuffix: true,
                        locale: fr
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
